import React, {
    forwardRef,
    MutableRefObject,
    ReactNode,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import './send-notification-editor.scss'
import { useJourneyContext } from '../context'
import { NotificationDto } from '../../notifications'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import { NotificationBuilderRef } from '../../../components/notification-builder/notification-builder'
import { NotificationBuilderLevel } from '../../../components/notification-builder/enums'
import {
    buildSubmittedNotificationPackages,
    validateSubmit as validateNotificationBuilder,
} from '../../../components/notification-builder/helpers'
import {
    ActionStep,
    EditableNode,
    getSendParams,
    isSendWithItemStrategyActionStep,
    JourneyNode,
    NodeEditorRef,
} from '../types/journey-nodes'
import { getJourneyTriggerItemType } from '../node-types/custom-node/helpers'
import { useItemStrategyOptions } from '../hooks/use-item-strategy-options'
import type { JourneyNotificationBuilderConfig } from './types'
import { MacroType } from '@pushly/aqe/lib/components'
import { DomainNotificationBuilderView } from '../../../components/notification-builder/views/domain-notification-builder-view'
import { JourneyBuilderMode } from '../enums'
import { handleNodeParamsUpdate } from '../helpers'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { NotificationCatalogItemPickProps } from '../../../components/notification-builder/views/internals'

type SendNotificationEditorProps = {
    ref?: MutableRefObject<NodeEditorRef>
    node: JourneyNode<ActionStep>
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    imageMacroToggleLabel?: string
    imageMacroValue?: string
    imageMacroIcon?: ReactNode
    builderProps?: Partial<JourneyNotificationBuilderConfig>
}

export const SendNotificationEditor = forwardRef<NodeEditorRef, SendNotificationEditorProps>((props, ref) => {
    const { node } = props

    const [state, _dispatch] = useJourneyContext()

    const appState = useService(AppState)

    const journey = state.journey
    const domain = state.domain
    const flags = appState.flags

    const action = node.data.step

    const sendParams = getSendParams(action)
    const notification = sendParams?.notification

    const hasItemStrategy = isSendWithItemStrategyActionStep(action)
    const currentSelectedStrategy = hasItemStrategy && journey ? action.configuration.params.item_strategy : undefined

    // For trigger-based campaigns, get itemType from trigger; for scheduled, use domain's ecommConfig
    const itemType =
        hasItemStrategy && journey ? getJourneyTriggerItemType(journey) ?? domain?.ecommConfig?.itemType : undefined
    const itemStrategy = useItemStrategyOptions({
        action,
        itemType,
        allNodes: state.nodes,
        currentNodeId: node.id,
        currentSelectedStrategy,
    })

    const builderConfig: JourneyNotificationBuilderConfig = useMemo(() => {
        const config: JourneyNotificationBuilderConfig = {
            domain,
            level: NotificationBuilderLevel.CAMPAIGN,
            mode: notification ? 'edit' : 'create',
            notifications: notification ? [NotificationDto.parse(notification)] : undefined,
            defaultChannels: state.journey?.channels,
            defaultSelectedNotifId: notification?.id ? Number(notification.id) : undefined,
            showWebhookOptions: false,
        }

        if (itemStrategy.isCatalog) {
            config.catalogItemType = itemType ?? undefined
            config.catalogItemPickStrategyOptions = itemStrategy.options
            config.selectedCatalogItemPickStrategy = itemStrategy.selected

            if (isSendWithItemStrategyActionStep(node.data.step) && node.data.step.configuration.params.item_strategy) {
                config.selectedCatalogItemPickStrategy = node.data.step.configuration.params.item_strategy
            }

            config.macroProps = {
                categories: {
                    urlFields: ['domain', 'device', 'location', 'ecomm', 'notification', 'secrets', 'custom'],
                    messageFields: ['location', 'ecomm', 'secrets', 'custom'],
                },
                imageMacroProps: {
                    label: itemStrategy.templates.imageMacroTagLabel,
                    tag: itemStrategy.templates.imageMacroTag,
                },
            }

            config.onInitialVariantInit = (variant) => {
                const contentModel = variant.getContent().getDefaultContent()
                contentModel.setLandingUrl(itemStrategy.templates.url)
                contentModel.setTitle(itemStrategy.templates.title)
                contentModel.setBody(itemStrategy.templates.body)
                contentModel.setImageUrl(itemStrategy.templates.image)
            }
        }

        return config
    }, [itemStrategy, itemType, notification, state.journey?.channels, domain])

    // Show catalog item options if we have a catalog item type OR if we have item strategy options
    const isCatalogItemNotification =
        !!builderConfig.catalogItemType || (itemStrategy.isCatalog && itemStrategy.options.length > 0)
    if (!domain) return <></>

    const notificationType = isCatalogItemNotification ? 'cart_notification' : 'standard_notification'

    const catalogItemPick: NotificationCatalogItemPickProps | false = useMemo(() => {
        if (!isCatalogItemNotification) return false

        return {
            options: builderConfig.catalogItemPickStrategyOptions ?? [],
            itemType,
            defaultSelectedOption: builderConfig.selectedCatalogItemPickStrategy,
        } satisfies NotificationCatalogItemPickProps
    }, [builderConfig, isCatalogItemNotification])

    const builderRef = useRef<NotificationBuilderRef>(null!)

    const onSubmit = useCallback<() => Promise<any>>(async () => {
        if (!builderRef.current) {
            console.warn('Builder ref is not initialized, skipping submit action')
            return
        }
        const builderState = builderRef.current.state

        if (state.mode === JourneyBuilderMode.READONLY) {
            return
        }

        const valid = await validateNotificationBuilder(builderState, domain, flags, false, false)
        if (!valid) {
            return
        }

        const builds = buildSubmittedNotificationPackages(domain, builderState, flags)
        if (!builds.length) {
            return
        }

        const nextItemStrategy = isCatalogItemNotification ? builderState.selectedCatalogItemPickStrategy : undefined
        const notificationParam = builds[0]

        return handleNodeParamsUpdate(
            node as EditableNode,
            stripUndefined({
                notification: notificationParam,
                title: notificationParam.template.channels.default.title,
                item_strategy: nextItemStrategy,
            }),
        )
    }, [
        domain,
        flags,
        isCatalogItemNotification,
        node,
        state.mode,
        validateNotificationBuilder,
        buildSubmittedNotificationPackages,
    ])

    useImperativeHandle(ref, () => ({ onSubmit }))

    const builderProps = {
        ...builderConfig,
        ...props.builderProps,
    }

    return (
        <div id="send-notification-editor">
            <DomainNotificationBuilderView
                ref={builderRef}
                formType="send"
                formMode="create"
                journey={journey}
                journeyNotificationType={notificationType}
                isInDrawer={true}
                catalogItemPick={catalogItemPick}
                hidePageHeader={true}
                hidePushlyAiToggle={true}
                hidePageAnalysisToggle={true}
                hideAudienceSelection={true}
                hideEstimatedReach={true}
                hideDeliveryChannels={true}
                hideScheduling={true}
                hideSubmit={true}
                onSubmit={noop}
                onCancel={noop}
                builderProps={{
                    ...builderProps,
                    macroProps: builderProps.macroProps,
                }}
            />
        </div>
    )
})
